import configs from '../../configs'
import mutations from './mutations'
import auth from '@/auth'
import { bus } from '@/main'
import { RTL_LANGUAGES } from '@/utils/constants'

const { product, theme } = configs
const { globalTheme } = theme

// Initialize RTL based on stored language
const storedLang = localStorage.selectedLanguage
const isRTL = storedLang ? RTL_LANGUAGES.includes(storedLang) : false

// Set document direction on initialization (not just when changing language)
document.documentElement.dir = isRTL ? 'rtl' : 'ltr'

function loadPrefill() {
  try { return JSON.parse(localStorage.prefillDataHeader || '{}') || {} } catch { return {} }
}
const prefill = loadPrefill()

// state initial values
const state = {
  product,

  // app theme dark or light
  globalTheme,

  // themes and layout configurations
  isRTL,

  // custom vars
  selectedLang: 'en',
  isLoggedIn: false,
  tableFooterOptions: {
    itemsPerPageOptions: [10, 25, 100, 250, 500]
  },
  userRoles: auth.fetchRoles(),
  userPrimaryRole: (() => {
    try {
      return JSON.parse(localStorage.getItem('userData'))?.primaryRole ?? null
    } catch { return null }
  })(),
  defaultFrontend: {
    baseUrlRest: '',
    defaultLocale: '',
    logServerUrl: '',
    metricServerUrl: '',
    frontendUrl: '',
    visualisationServerUrl: '',
    environmentName: ''
  },
  savedLocations: [],
  userSettings: {
    general: {
      selectedLanguage: 'en'
    },
    visuals: {
      selectedTheme: 'light',
      selectedColorScheme: 'default'
    },
    display: {
      showCreatedOn: true,
      showModifiedOn: true,
      showId: true,
      showComments: false,
      showCopyPaste: false,
      showFinishedOn: false,
      showStepType: false,
      showVisualId: true
    },
    favorites: []
  },
  tagHeaderFilter: prefill.tagHeaderFilter || { tags: [], ids: [] },

  snackbarQueue: [],
  processUndoStack: [],
  processRedoStack: [],
  processCurrent: {},
  formValid: false,
  resourceToRestoreAfterDelete: null,
  otherEditingSessions: [],
  stepCache: {},
  isOAuth: false,
  navigationSource: null  // 'favorites' | 'menu' | null
}

const getters = {
  formValid(state) {
    return state.formValid
  },
  selectedLang(state) {
    return state.selectedLang
  },
  isLoggedIn(state) {
    return state.isLoggedIn
  },
  tableFooterOptions(state) {
    return state.tableFooterOptions
  },
  userRoles(state) {
    return state.userRoles
  },
  userPrimaryRole(state) {
    return state.userPrimaryRole
  },
  defaultFrontend(state) {
    return state.defaultFrontend
  },
  globalTheme(state) {
    return state.globalTheme
  },
  savedLocations(state) {
    return state.savedLocations
  },
  userSettings(state) {
    return state.userSettings
  },
  currentSnackbar(state) {
    return state.snackbarQueue.length ? state.snackbarQueue[state.snackbarQueue.length - 1] : []
  },
  stepCache: (state) => (processId, stepId) =>
    state.stepCache[processId]?.[stepId] || null,

  tagHeaderFilter(state) {
    return state.tagHeaderFilter
  },

  isOAuth(state) {
    return state.isOAuth
  },

  isRTL(state) {
    return state.isRTL
  },
  favorites(state) {
    return state.userSettings?.favorites || []
  },
  isFavorite: (state) => (routeName) => {
    return (state.userSettings?.favorites || []).includes(routeName)
  },
  navigationSource: (state) => state.navigationSource
}

const actions = {
  formValid({ commit }, data) {
    commit('formValid', data)
  },
  selectedLang({ commit }, data) {
    commit('selectedLang', data)
  },
  isLoggedIn({ commit }, data) {
    commit('isLoggedIn', data)
  },
  defaultFrontend({ commit }, data) {
    commit('defaultFrontend', data)
  },
  globalTheme({ commit }, data) {
    commit('globalTheme', data)
  },
  savedLocationsSave({ commit }, data) {
    commit('savedLocations', data)
  },
  userSettingsSave({ commit }, data) {
    commit('userSettings', data)
  },
  addSnackbar({ commit }, payload) {
    commit('pushSnackbar', payload)
  },
  removeSnackbar({ commit }) {
    commit('shiftSnackbar')
  },
  saveProcessState({ commit }, process) {
    commit('processPushUndo', process)
    commit('processClearRedo')
  },
  undoProcess({ commit }) {
    commit('processUndo')
  },
  redoProcess({ commit }) {
    commit('processRedo')
  },
  saveResourceFromRestoreAfterDelete({ commit }, resource) {
    commit('saveResourceToRestoreAfterDelete', resource)
  },
  clearResourceFromRestoreAfterDelete({ commit }) {
    commit('clearResourceFromRestoreAfterDelete')
  },
  saveOtherEditingSessions({ commit }, sessions) {
    commit('saveOtherEditingSessions', sessions)
  },
  clearEditingSessions({ commit }) {
    commit('clearEditingSessions')
  },
  setStepCache({ commit }, { processId, stepId, data }) {
    commit('setStepCache', { processId, stepId, data });
  },
  clearStepCache({ commit }, { processId, stepId }) {
    commit('clearStepCache', { processId, stepId });
  },
  clearStepCacheOnUnmount({ commit }, { processId }) {
    commit('clearStepCacheOnUnmount', { processId });
  },
  saveTagHeaderFilter({ commit }, { tags, ids }) {
    commit('setTagHeaderFilter', { tags, ids })
  },
  clearTagHeaderFilter({ commit }) {
    commit('clearTagHeaderFilter')
  },
  userRolesSave({ commit }, roles) {
    commit('setUserRoles', roles)
  },
  userPrimaryRoleSave({ commit }, role) {
    commit('setUserPrimaryRole', role)
  },
  isOAuthSave({ commit }, data) {
    commit('setOAuth', data)
  },
  toggleFavorite({ commit }, menuItem) {
    if (!menuItem?.link) {
      console.warn('toggleFavorite called without valid menuItem.link', menuItem)
      return
    }
    commit('toggleFavorite', menuItem.link)
    bus.$emit('saveUserProfile')
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
